import React, { useState } from "react";
import { ReceiptIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGetChargeQuery, useGetChargesQuery } from "@/store/rest_api";
import ChargeDetailsSheet from "@/components/pages/billing/ChargeDetailsSheet";
import { CHARGE_TYPE_LABELS, ChargeType } from "@/enum/ChargeType";
import { CHARGE_PAGE_SIZE_OPTIONS, chargeUserName, formatChargeAmount } from "@/util/charge";
import { formatDateTime } from "@/util/date";

const ALL_TYPES = "ALL";

export function ChargeHistory() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(CHARGE_PAGE_SIZE_OPTIONS[0]);
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);
  const { data, isLoading } = useGetChargesQuery({
    page,
    size: pageSize,
    type: typeFilter === ALL_TYPES ? undefined : (typeFilter as ChargeType),
  });
  const { data: selectedCharge, isFetching: isFetchingCharge } = useGetChargeQuery(selectedChargeId ?? "", {
    skip: !selectedChargeId,
  });

  const charges = data?.items ?? [];

  const handleTypeChange = (value: string) => {
    setTypeFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPage(0);
  };

  const handleSheetOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedChargeId(null);
    }
  };

  return (
    <div className="container p-4 text-foreground">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <ReceiptIcon className="h-5 w-5" />
              Charges
            </CardTitle>
            <CardDescription>Everything your organisation was charged for, and who used it.</CardDescription>
          </div>
          <Select value={typeFilter} onValueChange={handleTypeChange}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All types</SelectItem>
              {Object.values(ChargeType).map((chargeType) => (
                <SelectItem key={chargeType} value={chargeType}>
                  {CHARGE_TYPE_LABELS[chargeType]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : charges.length === 0 ? (
            <div className="py-12 text-center">
              <ReceiptIcon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">No charges yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {charges.map((charge) => (
                    <TableRow
                      key={charge.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedChargeId(charge.id)}
                    >
                      <TableCell className="font-medium">{CHARGE_TYPE_LABELS[charge.type] ?? charge.type}</TableCell>
                      <TableCell className="text-muted-foreground">{charge.description}</TableCell>
                      <TableCell>{chargeUserName(charge.user)}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(charge.createdAt)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatChargeAmount(charge.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data && data.totalElements > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Results per page</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[80px] tabular-nums">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHARGE_PAGE_SIZE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={String(option)}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {data.totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    Page {data.page + 1} of {data.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={data.page === 0}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((current) => current + 1)}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ChargeDetailsSheet
        open={!!selectedChargeId}
        onOpenChange={handleSheetOpenChange}
        charge={selectedCharge}
        isLoading={isFetchingCharge}
      />
    </div>
  );
}
